import { ArrowLeft, ArrowRight } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getProdutosByUser } from '../../services/firestoreService';
import { salvarCliente } from '../../services/userService';
import { Atendimento, ClienteFormData, Produto, ProdutoReserva, ReservaFormData } from '../../types';
import ClienteForm from '../Clientes/ClienteForm';
import { CustomModal } from '../Common/Modal';
import ReservaForm from '../Reservas/ReservaForm';
import { TimelineHorizontal } from './TimelineHorizontal';
import './AtendimentoToReserva.css';

interface AtendimentoToReservaProps {
    show: boolean;
    onHide: () => void;
    atendimento: Atendimento | null;
    onReservaCreated?: () => void;
    onCreateReserva: (reservaData: ReservaFormData) => Promise<{ id: string } | undefined>;
}

type Step = 'cliente' | 'reserva' | 'loading';

const AtendimentoToReserva: React.FC<AtendimentoToReservaProps> = ({
    show,
    onHide,
    atendimento,
    onReservaCreated,
    onCreateReserva
}) => {

    const [currentStep, setCurrentStep] = useState<Step>('cliente');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [reservaInitialData, setReservaInitialData] = useState<ReservaFormData | null>(null);
    const [clienteId, setClienteId] = useState<string>('');
    const [clienteNome, setClienteNome] = useState<string>('');
    const [precisaCadastrarCliente, setPrecisaCadastrarCliente] = useState(false);

    useEffect(() => {
        if (show && atendimento) {
            const temCliente = !!atendimento.clienteId;
            setPrecisaCadastrarCliente(!temCliente);
            setClienteNome(atendimento.clienteNome);

            if (temCliente) {
                setClienteId(atendimento.clienteId!);
                prepararReserva(atendimento.clienteId!);
            } else {
                setCurrentStep('cliente');
            }
        } else if (!show) {
            resetState();
        }
    }, [show, atendimento?.id]);

    const resetState = () => {
        setCurrentStep('cliente');
        setReservaInitialData(null);
        setClienteId('');
        setClienteNome('');
        setError('');
        setPrecisaCadastrarCliente(false);
    };

    const prepararReserva = async (clienteIdFinal: string) => {
        if (!atendimento) return;

        setLoading(true);
        setError('');
        setCurrentStep('loading');

        try {
            const userId = atendimento.userId;
            const produtosResult = await getProdutosByUser(userId);

            if (!produtosResult.success || !produtosResult.data) {
                setError('Erro ao carregar produtos');
                setLoading(false);
                return;
            }

            const produtos: Produto[] = produtosResult.data;

            const produtosReserva: ProdutoReserva[] =
                (atendimento.produtosSelecionados || []).map(prodAtend => {
                    const produto = produtos.find(p => p.id === prodAtend.id);
                    const preco = produto?.preco || 0;

                    return {
                        produtoId: prodAtend.id,
                        produtoTitulo: prodAtend.titulo,
                        produtoTamanho: prodAtend.tamanho,
                        produtoCor: prodAtend.cor,
                        produtoCodigoProduto: prodAtend.codigoProduto || '',
                        quantidade: 1,
                        precoUnitario: preco,
                        precoTotal: preco,
                        produtoObservacoes: produto?.observacao || ''
                    };
                });

            const dadosReserva: ReservaFormData = {
                clienteId: clienteIdFinal,
                produtos: produtosReserva,
                dataRetirada: '',
                dataDevolucao: '',
                status: 'reservado',
                observacoes: atendimento.observacoes || '',
                funcionarioId: atendimento.funcionarioId,
                funcionarioNome: atendimento.funcionarioNome
            };

            setReservaInitialData(dadosReserva);
            setCurrentStep('reserva');

        } catch (err: any) {
            setError(err.message || 'Erro ao preparar reserva');
            toast.error('Erro ao preparar reserva');
        } finally {
            setLoading(false);
        }
    };

    const handleClienteSave = async (clienteData: ClienteFormData) => {
        try {
            const result = await salvarCliente(clienteData);

            if (result.success && result.id) {
                const novoClienteId = result.id;
                // Atualiza os estados primeiro
                setClienteId(novoClienteId);
                setClienteNome(clienteData.nome);
                setPrecisaCadastrarCliente(false);
                toast.success('Cliente cadastrado com sucesso!');
                
                // Avança automaticamente para preparar a reserva
                // Usa requestAnimationFrame para garantir que o estado foi atualizado
                requestAnimationFrame(() => {
                    prepararReserva(novoClienteId);
                });
            } else {
                toast.error(result.error || 'Erro ao cadastrar cliente');
            }

        } catch (error: any) {
            toast.error('Erro ao salvar cliente');
        }
    };

    const handleReservaSave = async (reservaData: ReservaFormData) => {
        const result = await onCreateReserva(reservaData);
        if (result?.id) {
            toast.success('Reserva criada com sucesso!');
            onHide();
            if (onReservaCreated) onReservaCreated();
        }
        return result;
    };

    // Navegação entre steps
    const handleNext = () => {
        if (currentStep === 'cliente' && clienteId) {
            prepararReserva(clienteId);
        }
    };

    const handleBack = () => {
        if (currentStep === 'reserva') {
            setCurrentStep('cliente');
            setReservaInitialData(null);
        }
    };

    // Verifica se pode avançar
    const canGoNext = () => {
        if (currentStep === 'cliente') {
            return !!clienteId || !precisaCadastrarCliente;
        }
        return false;
    };

    // Verifica se pode voltar
    const canGoBack = () => {
        return currentStep === 'reserva';
    };

    if (!atendimento) return null;

    const getHeaderLabel = () => {
        if (currentStep === 'cliente') return 'Cadastrar Cliente';
        if (currentStep === 'loading') return 'Preparando Reserva';
        return 'Criar Reserva';
    };

    // Renderiza modal de cliente separado quando necessário
    if (currentStep === 'cliente' && precisaCadastrarCliente && show) {
        return (
            <ClienteForm
                show={true}
                onHide={() => {
                    // Se o cliente já foi salvo, não fecha tudo, apenas avança
                    if (clienteId) {
                        prepararReserva(clienteId);
                    } else {
                        onHide();
                    }
                }}
                onSave={async (clienteData) => {
                    await handleClienteSave(clienteData);
                }}
                cliente={null}
                initialNome={clienteNome}
                embedded={true}
                customHeaderContent={
                    <TimelineHorizontal
                        currentStep={currentStep}
                        precisaCadastrarCliente={precisaCadastrarCliente}
                    />
                }
            />
        );
    }

    // Renderiza modal de reserva separado quando necessário
    if (currentStep === 'reserva' && reservaInitialData && show) {
        return (
            <ReservaForm
                show={true}
                onHide={onHide}
                onSave={handleReservaSave}
                reserva={null}
                initialData={reservaInitialData}
                customHeaderContent={
                    <TimelineHorizontal
                        currentStep={currentStep}
                        precisaCadastrarCliente={precisaCadastrarCliente}
                    />
                }
            />
        );
    }

    return (
        <CustomModal
            show={show}
            onHide={onHide}
            centered
            size="xl"
            headerLabel={getHeaderLabel()}
            showButtonsFooter={false}
        >
            {/* TIMELINE NO TOPO */}
            <TimelineHorizontal
                currentStep={currentStep}
                precisaCadastrarCliente={precisaCadastrarCliente}
            />

            {/* MENSAGEM DE ERRO */}
            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            {/* CONTEÚDO DOS STEPS */}
            <div className="step-container">
                {/* STEP: CLIENTE JÁ CADASTRADO */}
                {currentStep === 'cliente' && !precisaCadastrarCliente && (
                    <div className="step-content">
                        <div className="text-center py-4">
                            <div className="mb-3">
                                <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success text-white" 
                                     style={{ width: '60px', height: '60px', fontSize: '24px' }}>
                                    ✓
                                </div>
                            </div>
                            <h5 className="mb-2">Cliente já cadastrado</h5>
                            <p className="text-muted mb-0">
                                <strong>{clienteNome}</strong>
                            </p>
                            <p className="text-muted mt-2">
                                Clique em "Avançar" para continuar com a criação da reserva.
                            </p>
                        </div>
                    </div>
                )}

                {/* STEP: LOADING */}
                {currentStep === 'loading' && (
                    <div className="step-content text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Preparando dados da reserva...</p>
                    </div>
                )}
            </div>

            {/* BOTÕES DE NAVEGAÇÃO */}
            <div className="navigation-buttons">
                <Button
                    variant="outline-secondary"
                    onClick={handleBack}
                    disabled={!canGoBack() || loading}
                    className="d-flex align-items-center gap-2"
                >
                    <ArrowLeft size={18} />
                    Voltar
                </Button>

                {currentStep === 'cliente' && (
                    <Button
                        variant="primary"
                        onClick={handleNext}
                        disabled={!canGoNext() || loading}
                        className="d-flex align-items-center gap-2"
                    >
                        Avançar
                        <ArrowRight size={18} />
                    </Button>
                )}
            </div>
        </CustomModal>
    );
};

export default AtendimentoToReserva;
